import { action, computed, observable } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { isNotNil } from '@/utils/object-util'
import { toStream } from '@/utils/mobx-util'
import { filterNonNil } from '@/utils/rx-util'
import { switchMap, EMPTY } from 'rxjs'
import { tap } from 'rxjs/operators'
import { match } from '@/utils/pattern-match-util'
import type {
  SupportingItemViewModel,
  TaxInfoGatheringViewModel,
} from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringViewModel'
import { ViewModelList } from '@/infra/view-model-list'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { TaxInfoGatheringPageSectionViewModel } from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringPageSectionViewModel'

/**
 * View-model for the tax info gathering page.
 * This is used specifically while the job is in the info gathering step.
 */
export class TaxInfoGatheringPageViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Sections view models.
   *
   * @private
   */
  private readonly _sectionVms

  /**
   * The currently selected item.
   */
  @observable accessor selectedItemVm: SupportingItemViewModel | null = null

  /**
   * Stream for the selected item. Emits the current value on `subscribe`.
   */
  readonly selectedItemVm$ = toStream(() => this.selectedItemVm, true).pipe(
    filterNonNil(),
  )

  /**
   * Which direction we are navigating.
   */
  @observable accessor direction: 'forward' | 'backward' = 'forward'

  /**
   * Modal state for the navigation on smaller screens.
   */
  readonly navigationModalVm = new ModalViewModel()

  /**
   * Constructs a new info gathering page view model.
   *
   * @param infoGatheringVm The parent view model that hosts this one.
   */
  constructor(readonly infoGatheringVm: TaxInfoGatheringViewModel) {
    this._sectionVms = new ViewModelList({
      models: () => this.infoGatheringVm.checklist?.sections.items ?? [],
      create: (section) => {
        return new TaxInfoGatheringPageSectionViewModel(
          () => section.name,
          () =>
            section.requestedSupportingItems
              .map((item) => this.infoGatheringVm.getItemVm(item.id))
              .filter(isNotNil),
        )
      },
    })

    this._lifecycle.manage(this._sectionVms)

    // Go to the next item when the current item instructs us to.
    this._lifecycle.manage(
      this.selectedItemVm$.pipe(
        switchMap((itemVm) =>
          match(itemVm, 'type', {
            PersonalInfo: (vm) => vm.next$,
            SpousalInfo: (vm) => vm.next$,
            PersonalTaxRefundMethod: (vm) => vm.next$,
            BusinessInfo: (vm) => vm.next$,
            BusinessTaxRefundMethod: (vm) => vm.next$,
            Notes: (vm) => vm.next$,
            [match.DEFAULT]: () => EMPTY,
          }),
        ),
        tap(() => {
          const currentItem = this.selectedItemVm

          // If we're on the last item, don't do anything.
          if (currentItem && this.isLastItem(currentItem)) {
            return
          }

          this.selectNextItem()
        }),
      ),
    )
  }

  /**
   * The section view models.
   */
  @computed get sectionVms() {
    return this._sectionVms.items
  }

  /**
   * Gets the counts of items that are completed and incompleted.
   */
  @computed get counts() {
    const completion = this.infoGatheringVm.completion
    const total = this.allOrderedItems.length

    let incompleted = 0
    let completed = 0

    switch (completion.type) {
      case 'Completed': {
        incompleted = 0
        completed = completion.items.length
        break
      }
      case 'Incomplete': {
        incompleted = completion.incomplete.length
        completed = completion.completed.length

        break
      }
      case 'Empty': {
        completed = 0
        incompleted = 0
      }
    }

    const completionPercentage = total === 0 ? 0 : (completed / total) * 100

    return {
      incompleted,
      completed,
      total,
      completionPercentage,
    } as const
  }

  /**
   * All items, ordered based on their sections.
   */
  @computed get allOrderedItems() {
    return this.sectionVms.flatMap((sectionVm) => sectionVm.items)
  }

  /**
   * Gets the current index (zero-based) of the selected item.
   */
  @computed get currentStepIndex() {
    const itemVm = this.selectedItemVm
    if (!itemVm) {
      return -1
    }
    return this.allOrderedItems.indexOf(itemVm)
  }

  /**
   * Whether the item is the last item in the ordered list of items.
   *
   * @param itemVm
   */
  isLastItem(itemVm: SupportingItemViewModel) {
    return (
      this.allOrderedItems.indexOf(itemVm) === this.allOrderedItems.length - 1
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    this.expandSelectedItemSection()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Expands the section of the currently selected item and selects the
   * proper tab.
   */
  @action.bound
  expandSelectedItemSection() {
    const selectedItemVm = this.selectedItemVm
    if (!selectedItemVm) {
      return
    }

    const section = this.sectionVms.find((s) =>
      s.items.includes(selectedItemVm),
    )

    section?.setExpanded(true)
  }

  /**
   * Selects an item to display.
   *
   * @param itemVm
   */
  @action.bound
  selectItem(itemVm: SupportingItemViewModel | null) {
    if (this.selectedItemVm === itemVm) {
      return
    }

    const indexOfCurrent = this.currentStepIndex
    const indexOfNew = itemVm ? this.allOrderedItems.indexOf(itemVm) : -1
    this.direction = indexOfNew >= indexOfCurrent ? 'forward' : 'backward'
    this.selectedItemVm = itemVm
    this.expandSelectedItemSection()
  }

  /**
   * Selects the previous item.
   */
  @action.bound selectPreviousItem() {
    const allItems = this.allOrderedItems

    const selectedItemVm = this.selectedItemVm
    if (!selectedItemVm) {
      const previousItem = allItems[allItems.length - 1]
      this.selectItem(previousItem)
      return
    }

    const index = allItems.indexOf(selectedItemVm)

    // Reset to the last one if out of range.
    if (index <= 0) {
      const previousItem = allItems[allItems.length - 1]
      this.selectItem(previousItem)
      return
    }

    const previousItem = allItems[index - 1]
    this.selectItem(previousItem)
  }

  /**
   * Selects the next item.
   */
  @action.bound selectNextItem() {
    const allItems = this.allOrderedItems

    const selectedItemVm = this.selectedItemVm
    if (!selectedItemVm) {
      const nextItem = allItems[0]
      this.selectItem(nextItem)
      return
    }

    const index = allItems.indexOf(selectedItemVm)

    // Reset to the first one if out of range.
    if (index < 0 || index >= allItems.length - 1) {
      const nextItem = allItems[0]
      this.selectItem(nextItem)
      return
    }

    const nextItem = allItems[index + 1]
    this.selectItem(nextItem)
  }
}
